/**
 * MUI UnitSwitch Component
 * 
 * This component is the Material UI equivalent of the original UnitSwitch component.
 * It provides a toggle switch between feet and meters units.
 */

import React from 'react';
import { Box, ToggleButtonGroup, ToggleButton, styled } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import ReducersIndex from 'reduxModules/ducks';
import { buttonThemes } from 'globalConstants/Themes';
import { UnitSwitchProps } from 'types/components/molecules';
import { AnyAction } from '@reduxjs/toolkit';

// Define the type for units
type UnitType = 'feet' | 'meters';

/**
 * Styled container for the toggle switch to match DesignsList ViewControls pattern
 */
const ToggleContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '4px',
  background: theme.palette.mode === 'dark'
    ? theme.palette.grey[700]
    : theme.palette.grey[50],
  borderRadius: '8px',
  padding: '4px',
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: theme.palette.mode === 'dark'
    ? 'inset 0 1px 3px rgba(0, 0, 0, 0.4)'
    : 'inset 0 1px 2px rgba(0, 0, 0, 0.05)',
  height: '36px',
  alignItems: 'center',
}));

/**
 * Styled ToggleButton component to match DesignsList ViewButton pattern
 */
const StyledToggleButton = styled(ToggleButton)<{ checked: boolean }>(({ theme, checked }) => ({
  height: '28px',
  padding: '0 12px',
  borderRadius: theme.shape.borderRadius,
  border: 'none',
  background: checked
    ? theme.palette.background.default
    : 'transparent',
  overflow: 'hidden',
  color: checked ? theme.palette.text.primary : theme.palette.text.secondary,
  textOverflow: 'ellipsis',
  fontFamily: '"Open Sans"',
  fontSize: '14px',
  fontStyle: 'normal',
  fontWeight: checked ? 600 : 500,
  lineHeight: '150%',
  letterSpacing: '-0.28px',
  fontFeatureSettings: "'liga' off, 'clig' off",
  textTransform: 'none',
  minWidth: 'auto',
  boxShadow: checked
    ? theme.palette.mode === 'dark'
      ? '0px 1px 3px rgba(0, 0, 0, 0.3)'
      : '0px 1px 2px rgba(0, 0, 0, 0.1)'
    : 'none',
  transition: 'all 0.2s ease-in-out',
  '&.Mui-selected': {
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.primary,
    fontWeight: 600,
    boxShadow: theme.palette.mode === 'dark'
      ? '0px 1px 3px rgba(0, 0, 0, 0.3)'
      : '0px 1px 2px rgba(0, 0, 0, 0.1)',
  },
  '&:hover': {
    backgroundColor: checked
      ? theme.palette.background.default
      : theme.palette.action.hover,
    color: checked ? theme.palette.text.primary : theme.palette.text.primary,
  },
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: '2px',
  },
}));

/**
 * MUI UnitSwitch component
 * 
 * @param props - Component props
 * @returns The MUI UnitSwitch component
 */
const MuiUnitSwitch: React.FC<UnitSwitchProps> = (props) => {
  const dispatch = useDispatch();
  
  // Get current units from Redux state, cast as UnitType
  const units = useSelector((state: any) => 
    state.ui.units
  ) as UnitType;

  /**
   * Handle unit change
   * 
   * @param event - The change event
   * @param newUnits - The new units value
   */
  const handleUnitChange = (
    event: React.MouseEvent<HTMLElement>, 
    newUnits: UnitType | null
  ): void => {
    // Prevent deselection (null value)
    if (newUnits !== null) {
      dispatch(
        ReducersIndex.uiReducers.setUnits(newUnits) as unknown as AnyAction
      );
    }
  };

  return (
    <ToggleContainer>
      <ToggleButtonGroup
        value={units}
        exclusive
        onChange={handleUnitChange}
        aria-label="unit switch"
        sx={{ 
          '& .MuiToggleButtonGroup-grouped': {
            border: 'none',
          }
        }}
      >
        <StyledToggleButton value="feet" aria-label="feet" checked={units === 'feet'}>
          Feet
        </StyledToggleButton>
        <StyledToggleButton value="meters" aria-label="meters" checked={units === 'meters'}>
          Meters
        </StyledToggleButton>
      </ToggleButtonGroup>
    </ToggleContainer>
  );
};

export type { UnitSwitchProps };
export default MuiUnitSwitch;
